// CSC-ONLY proposal pipeline. Extracted from cscProposalTab.jsx so the new
// Combined tab and the old Proposal tab share ONE source of truth for the
// commercial 29-page proposal. COMMERCIAL math/markup only — must NOT import
// or reuse any RSC code (useRscPricing saving math / proposalEngine.js).
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CSC_PROPOSAL_TARIFFS } from '../../hooks/useCscProposalMath';

const fmt = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmt3 = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const fmtDMY = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};
export const todayDMY = () => fmtDMY(new Date());
export const plus30DMY = () => { const d = new Date(); d.setDate(d.getDate() + 30); return fmtDMY(d); };

// Unified inverter sizing rule (per spec, 2026-05-20):
//   ≤26 kWp   → look up ATAP Residential Pricing row (phase-specific
//               inverterSize / inverterSize3p); fall back to
//               Math.ceil(kwp/1.35) if no ATAP row matches
//   ~96 kWp   → 72 kW (known product spec: 150 × 640W → GoodWe/Solis 72K)
//   otherwise → smallest 5-kW multiple where kWp/kW < 1.35
// Mirrors autoFillInverter in commercialSolarCalculator.jsx — keep in sync.
export function autoInverterKw(kwp, atapPrices = [], phaseMode = 'single') {
    const k = Number(kwp) || 0;
    if (k <= 0) return 0;
    if (k <= 26) {
        const match = (atapPrices || []).find(p => Math.abs(p.kwp - k) < 0.05);
        if (match) {
            const str = phaseMode === '3phase' ? match.inverter3p : match.inverter;
            const m = /(\d+(?:\.\d+)?)\s*k/i.exec(String(str || ''));
            if (m) return Number(m[1]);
        }
        return Math.ceil(k / 1.35);
    }
    if (k > 95 && k <= 96.1) return 72;
    return Math.ceil(k / 1.35 / 5) * 5;
}

// SST = 6% on the 20% portion of the system price (same formula as the
// commercial quotation in tools/formGenerator.jsx → cSst = c20 × 0.06).
// Effective rate on the full price = 1.2%.
const SST_RATE = 0.06;

// Malaysia state → Global Horizontal Irradiance (kWh/m²/day, SolarGIS map).
// Drives the Site Solar Data page. PSH (peak sun hours) is held at 3.4 to
// match the Bill tab / commercial calculator (useCscProposalMath.PEAK_SUN_HOURS),
// so customers see both the geographic GHI *and* the conservative PSH the
// projection actually uses.
export const MY_STATE_GHI = {
    'perlis':           { name: 'Perlis',           ghi: 4.6 },
    'kedah':            { name: 'Kedah',            ghi: 4.6 },
    'penang':           { name: 'Penang',           ghi: 4.4 },
    'perak':            { name: 'Perak',            ghi: 4.5 },
    'selangor':         { name: 'Selangor',         ghi: 4.4 },
    'kl':               { name: 'Kuala Lumpur',     ghi: 4.4 },
    'putrajaya':        { name: 'Putrajaya',        ghi: 4.4 },
    'n9':               { name: 'Negeri Sembilan',  ghi: 4.6 },
    'melaka':           { name: 'Melaka',           ghi: 4.6 },
    'johor':            { name: 'Johor',            ghi: 4.4 },
    'pahang':           { name: 'Pahang',           ghi: 4.6 },
    'terengganu':       { name: 'Terengganu',       ghi: 4.8 },
    'kelantan':         { name: 'Kelantan',         ghi: 4.6 },
    'sabah':            { name: 'Sabah',            ghi: 4.8 },
    'sarawak':          { name: 'Sarawak',          ghi: 4.8 },
    'labuan':           { name: 'Labuan',           ghi: 4.8 },
};
const PERFORMANCE_RATIO = 0.8499;  // 84.99% — matches the irradiance page reference

// ── Build the substitution map from form state + math output ──
export function buildCscVarMap({ customer, system, math, sales, dates, financing, addOn, site, photos, maxDemand, batteryKwh, summarySavingMo }) {
    // Summary headline saving = REAL bill reduction passed in (Option B). Falls back to
    // the generation-revenue model when not provided. The 20-year TABLE is unaffected.
    const _summaryMo = (summarySavingMo != null && summarySavingMo > 0)
        ? summarySavingMo
        : ((math.years?.[0]?.grossRevenue || 0) / 12);
    const tariffEntry = CSC_PROPOSAL_TARIFFS.find((t) => t.rate === system.tariffRate);
    // Honor user override from the proposal form; otherwise fall back to
    // the tier-based auto-size (matches the sizing tab's INVERTER_TIERS).
    const inverterKw = Number(system.inverterKw) > 0
        ? Number(system.inverterKw)
        : autoInverterKw(system.kwp, system.atapPrices, system.phaseMode);
    const panelWatt = Number(system.panelWatt) || 640;
    const panelCount = Math.ceil((Number(system.kwp) || 0) * 1000 / panelWatt);
    // Multi-select inverter brand (per user, 2026-05-21). Falls back to the
    // single inverterBrand string, then to a kWp-derived default.
    const BRAND_LABELS = { solis: 'Solis', goodwe: 'GoodWe', huawei: 'Huawei' };
    const brandsList = Array.isArray(system.inverterBrands) && system.inverterBrands.length > 0
        ? system.inverterBrands
        : (system.inverterBrand ? [system.inverterBrand] : []);
    const mappedBrands = brandsList
        .map(b => BRAND_LABELS[String(b || '').toLowerCase()])
        .filter(Boolean);
    const inverterBrandLabel = mappedBrands.length > 0
        ? mappedBrands.join(' / ')
        : ((Number(system.kwp) || 0) >= 100 ? 'Huawei' : 'GoodWe / Solis');
    // BoM table renders panel count separately in the Solar Panel row; this
    // spec is only the inverter line — just size + brand, no redundant panels.
    const inverterSpec = `${inverterKw} kW ${inverterBrandLabel}`;

    // Optional battery storage. If set, adds a BoM row + a fragment on the
    // cover system-spec line so the customer sees the storage capacity.
    const batKwh = Number(batteryKwh) || 0;
    const batteryBomRow = batKwh > 0
        ? `<tr><td class="product">Lithium Battery (LiFePO4)</td><td class="brand">GoodWe Lynx / Huawei LUNA / equivalent Tier-1</td><td class="qty">${batKwh} kWh</td></tr>`
        : '';
    const batteryCoverFrag = batKwh > 0
        ? ` · <span class="sub" style="display:inline;margin-left:1mm;">+ ${batKwh} kWh battery</span>`
        : '';

    // Signing deposit rule (per user, 2026-05-21): ≤96 kWp → RM 2,000;
    // >96 kWp → RM 5,000. Applies to Outright, Loan, and Comparison table.
    const depositAmount = (Number(system.kwp) || 0) > 96 ? 5000 : 2000;

    // Add-on + SST (per user, 2026-05-21): add-on is an optional surcharge
    // (extra services, scope additions) added on top of the system price.
    // SST follows the commercial quotation method: 6% on the 20% portion.
    const baseSystemPrice = Number(system.systemCost) || 0;
    const addOnAmount = Number(addOn) || 0;
    const subtotal = baseSystemPrice + addOnAmount;
    const sstAmount = Math.round((subtotal * 0.20) * SST_RATE);
    const grandTotalPrice = subtotal + sstAmount;

    // Site irradiance: state-based GHI from the SolarGIS Malaysia map.
    // PSH is 3.4 (aligned to Bill tab / Calc); the page surfaces both numbers
    // so the customer sees the geographic upside vs. the value used in projection.
    const stateInfo = (site && MY_STATE_GHI[site]) || { name: '—', ghi: 4.5 };
    const sitePsh = 3.4;

    // Site photo grid — convert the array of data URLs into <img> tags for
    // the conditional "Site Design Summary" page.
    const photoList = Array.isArray(photos) ? photos.filter(Boolean) : [];
    const photosHtml = photoList.map((src, i) =>
        `<div class="photo-cell"><img src="${src}" alt="Site photo ${i + 1}"></div>`
    ).join('\n');

    // SELCO vs ATAP recommendation (corrected 2026-05-21):
    //   inverter kW > Maximum Demand (kW) → SELCO (limit export / 0 export)
    //   inverter kW ≤ Maximum Demand     → ATAP (sells excess at SMP)
    // Trigger is the AC inverter rating, not DC panel kWp — what TNB cares
    // about is the actual export capacity, which is gated by the inverter.
    const md = Number(maxDemand) || 0;
    const invForCheck = Number(inverterKw) || 0;
    let schemeName, schemeBlurb, schemeColor;
    if (md > 0 && invForCheck > md) {
        schemeName = 'SELCO';
        schemeBlurb = `Your inverter output (${invForCheck} kW) exceeds your Maximum Demand (${md} kW). Under TNB rules, the system runs as <strong>SELCO</strong> (Self-Consumption) with a <strong>limit export device</strong> / <strong>zero export</strong> setup — all generation is consumed on-site, none is exported to the grid.`;
        schemeColor = 'gold';
    } else if (md > 0) {
        schemeName = 'ATAP';
        schemeBlurb = `Your inverter output (${invForCheck} kW) is within your Maximum Demand (${md} kW). The system qualifies for <strong>ATAP</strong> — any excess generation exported to the grid is sold to TNB at the prevailing <strong>System Marginal Price (SMP)</strong> rate.`;
        schemeColor = 'blue';
    } else {
        schemeName = 'ATAP';
        schemeBlurb = `<em>Maximum Demand not specified.</em> Default assumption: <strong>ATAP</strong> — excess generation sold to TNB at SMP rate. If site MD &lt; inverter output, the project switches to <strong>SELCO</strong> (zero export).`;
        schemeColor = 'grey';
    }

    const m = math?.invalid ? {} : (math || {});

    // SARE projection table rows — TNB tariff escalates 8% every 3 years,
    // SARE rate stays flat; discount widens over time.
    //
    // Buyback formula — ported cell-for-cell from 181.76kwp.xlsx SPPA sheet:
    //   Buyback(year) = NPV(8%, U[year:20])   (Excel N7:N26 = NPV($N$5, U7:$U$26))
    // where U = Operating Income per year for the SARE provider:
    //   Revenue   R(year) = generation(year) × (FIXED SARE rate − 0.02)
    //   O&M       S(year) = S(year-1) × 1.03         (S8 = S7 × (1 + $S$5), S5 = 3%)
    //   Overhead  T(year) = T(year-1) × 1.03
    //   OpInc     U = R + S + T
    // Base O&M (Y1) — tiered by system size (Maintenance Pricing table):
    //   <100 kWp Klang Valley/Penang: 1.0% · RM 7/panel
    //   <100 kWp Outstation:          2.0% · RM 8/panel    (not exposed yet, defaults to KV/PG)
    //   100–499 kWp:                  0.75% · RM 5/panel
    //   500 kWp – 1 MWp:              0.50% · RM 5/panel
    //   >1 MWp:                       0.50% · RM 3.50/panel
    // Cleaning panel count uses Excel convention: kWp / 0.58 (≈ panels at 580 W).
    const sareSystemCost = Number(system.systemCost) || 0;
    const sareKwp = Number(system.kwp) || 0;
    const sareRateFlat = Number(m.sareTariff) || 0;
    const SARE_DISCOUNT = 0.08;
    const SARE_ESCALATION = 0.03;          // S5 — 3% annual escalation for O&M and Overhead
    const SARE_OVERHEAD_PCT = 0.05;        // T5
    const SARE_KWP_DIV = 0.58;             // cleaning panel-count divisor (kWp -> panels @ 580 W)
    const SARE_TARIFF_DEDUCT = 0.02;
    function sareOmRatesForKwp(kwp) {
        // Returns { maintPct, cleanRm } from the tier table.
        if (kwp >= 1000) return { maintPct: 0.005, cleanRm: 3.50 };
        if (kwp >= 500)  return { maintPct: 0.005, cleanRm: 5 };
        if (kwp >= 100)  return { maintPct: 0.0075, cleanRm: 5 };
        return { maintPct: 0.01, cleanRm: 7 };  // <100 kWp default Klang Valley/Penang
    }
    const { maintPct: SARE_OM_RATE, cleanRm: SARE_CLEAN_PER_PANEL } = sareOmRatesForKwp(sareKwp);
    const sareOmBase = -(SARE_OM_RATE * sareSystemCost) - (SARE_CLEAN_PER_PANEL * sareKwp / SARE_KWP_DIV);
    const yearsArr = m.years || [];
    const opIncome = yearsArr.map((y, idx) => {
        const gen = Number(y.generation) || 0;
        const revenue = gen * (sareRateFlat - SARE_TARIFF_DEDUCT);
        // O&M and Overhead escalate 3% per year (Y1 = base, Y2 = base×1.03, …)
        const escalator = Math.pow(1 + SARE_ESCALATION, idx);
        const om = sareOmBase * escalator;
        const overhead = -revenue * SARE_OVERHEAD_PCT * escalator;
        return revenue + om + overhead;
    });
    const sareBuyback = (currentYearIdx) => {
        // currentYearIdx is 0-based (year 1 → idx 0)
        let npv = 0;
        for (let i = currentYearIdx; i < opIncome.length; i++) {
            const period = i - currentYearIdx + 1;
            npv += opIncome[i] / Math.pow(1 + SARE_DISCOUNT, period);
        }
        return npv;
    };
    const sareRows = yearsArr.map((y, idx) => `
        <tr>
            <td>${y.year}</td>
            <td>${fmt(y.generation)}</td>
            <td>${fmt3(y.sare?.tariffYear || y.tariffYear || system.tariffRate)}</td>
            <td>${fmt3(m.sareTariff || 0)}</td>
            <td>${fmt3(y.sare?.discount ?? ((y.sare?.tariffYear || system.tariffRate) - (m.sareTariff || 0)))}</td>
            <td>${fmt(y.sare.annualCF)}</td>
            <td>${fmt(y.sare.annualCF / 12)}</td>
            <td>RM ${fmt(sareBuyback(idx))}</td>
        </tr>`).join('\n');

    // Loan-financed cashflow rows (Year | Gen | Savings | CA | GITA | Loan Payment | Net Cashflow | Cumulative)
    const loanRows = (m.years || []).map((y) => `
        <tr${y.year === m.loanBreakeven ? ' class="highlight"' : ''}>
            <td>${y.year}</td>
            <td>${fmt(y.generation)}</td>
            <td>${fmt(y.grossRevenue)}</td>
            <td>${y.caTaxSaving > 0 ? fmt(y.caTaxSaving) : '—'}</td>
            <td>${y.itaTaxSaving > 0 ? fmt(y.itaTaxSaving) : '—'}</td>
            <td>${y.loan.annualPayment > 0 ? fmt(y.loan.annualPayment) : '—'}</td>
            <td>${fmt(y.loan.annualCF)}</td>
            <td>${fmt(y.loan.cumulative)}</td>
        </tr>`).join('\n');

    // Outright projection table rows (Year | Gen | Tariff | Savings | Monthly | CA | GITA | Net Return)
    const outrightRows = (m.years || []).map((y) => `
        <tr${y.outright.cumulative >= 0 && (y.year === m.outrightBreakeven) ? ' class="highlight"' : ''}>
            <td>${y.year}</td>
            <td>${fmt(y.generation)}</td>
            <td>${fmt3(system.tariffRate)}</td>
            <td>${fmt(y.grossRevenue)}</td>
            <td>${fmt(y.grossRevenue / 12)}</td>
            <td>${y.caTaxSaving > 0 ? fmt(y.caTaxSaving) : '—'}</td>
            <td>${y.itaTaxSaving > 0 ? fmt(y.itaTaxSaving) : '—'}</td>
            <td>${fmt(y.outright.cumulative)}</td>
        </tr>`).join('\n');

    const grossRevenue20yr = (m.years || []).reduce((s, y) => s + y.grossRevenue, 0);

    // Comparison summary page title / subtitle — adapts to which financing
    // options the salesperson ticked. e.g. SARE off → "Outright vs Loan".
    const fopts = financing || {};
    const titleParts = [];
    const subtitleParts = [];
    if (fopts.includeSARE !== false) { titleParts.push('SARE'); subtitleParts.push('Tenant'); }
    if (fopts.includeOutright !== false) { titleParts.push('Outright'); subtitleParts.push('Owner'); }
    if (fopts.includeLoan !== false) { titleParts.push('Loan'); subtitleParts.push('Cashflow-Conscious'); }
    const comparisonTitle = titleParts.length >= 2 ? titleParts.join(' vs ') : (titleParts[0] || 'Financing Comparison');
    const comparisonSubtitle = subtitleParts.length ? `Recommended for ${subtitleParts.join(' · ')}` : '';

    return {
        // Customer
        customer_name: customer.name || '—',
        customer_phone: customer.phone || '',
        customer_email: customer.email || '',
        customer_address: customer.address || '',
        // System
        system_size_kwp: system.kwp || '—',
        panel_count: panelCount,
        inverter_spec: inverterSpec,
        inverter_brand_label: inverterBrandLabel,
        inverter_kw_only: `${inverterKw} kW`,
        panel_watt: String(panelWatt),
        tariff_rate: fmt3(system.tariffRate),
        tariff_b_rate: fmt3(system.tariffRate * 1.004),
        system_price: fmt(system.systemCost),
        loan_principal_display: fmt(financing.loanPrincipal || system.systemCost),
        deposit: fmt(depositAmount),
        // Add-on + SST breakdown
        addon_amount: fmt(addOnAmount),
        sst_amount: fmt(sstAmount),
        grand_total_price: fmt(grandTotalPrice),
        // Site irradiance (Solar Site Data page)
        site_state: stateInfo.name,
        site_ghi: stateInfo.ghi.toFixed(1),
        site_psh: sitePsh.toFixed(1),
        performance_ratio: (PERFORMANCE_RATIO * 100).toFixed(2),
        // Site photo grid (omitted if empty)
        site_photos_html: photosHtml,
        site_photos_count: String(photoList.length),
        // Site design / scheme recommendation
        max_demand_kw: md > 0 ? String(md) : '—',
        recommended_scheme: schemeName,
        scheme_blurb: schemeBlurb,
        scheme_color: schemeColor,
        roof_panel_count: String(panelCount),
        roof_kwp: String(system.kwp || '—'),
        // Battery (optional — empty strings when no battery)
        battery_kwh: batKwh > 0 ? String(batKwh) : '',
        battery_bom_row: batteryBomRow,
        battery_cover_frag: batteryCoverFrag,
        om_annual: fmt(system.omAnnual),
        // SARE block
        sare_system_price: fmt(system.systemCost),
        sare_tariff: fmt3(m.sareTariff || 0),
        sare_saving_per_kwh: fmt3((system.tariffRate || 0) - (m.sareTariff || 0)),
        sare_monthly_saving: fmt((m.years?.[0]?.sare.annualCF || 0) / 12),
        sare_yearly_saving: fmt(m.years?.[0]?.sare.annualCF || 0),
        sare_20yr_saving: fmt(m.total20yrSARE || 0),
        sare_projection_rows: sareRows,
        // Outright block
        outright_projection_rows: outrightRows,
        total_20yr_outright: fmt(m.total20yrOutright || 0),
        // Loan block
        loan_projection_rows: loanRows,
        loan_principal: fmt(m.loanPrincipal || system.systemCost),
        loan_rate_pct: ((m.loanRate || 0) * 100).toFixed(2),
        loan_term_years: ((m.loanTermMonths || 0) / 12).toFixed(1),
        loan_monthly_pmt: fmt(m.loanMonthlyPmt || 0),
        loan_annual_pmt: fmt(m.loanAnnualPmt || 0),
        loan_total_paid: fmt((m.loanAnnualPmt || 0) * ((m.loanTermMonths || 0) / 12)),
        loan_total_interest: fmt(((m.loanAnnualPmt || 0) * ((m.loanTermMonths || 0) / 12)) - (m.loanPrincipal || system.systemCost)),
        loan_total_20yr: fmt(m.total20yrLoan || 0),
        loan_breakeven_years: (() => {
            const n = m.loanBreakevenExact;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        loan_breakeven_years_no_incentives: (() => {
            const n = m.loanBreakevenExactNoIncentives;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        // Use Year-1 saving (more accurate for sales pitch; tariff escalator means
        // later years are larger, but Y1 is what the customer sees first).
        monthly_saving_avg: fmt(_summaryMo),
        yearly_saving_avg: fmt(_summaryMo * 12),
        // Payback shown as exact fractional years (e.g. 1.8) via linear
        // interpolation within the breakeven year. WITH CA + GITA and
        // WITHOUT CA + GITA both exposed for side-by-side comparison.
        payback_years: (() => {
            const n = m.outrightBreakevenExact;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        payback_years_no_incentives: (() => {
            const n = m.outrightBreakevenExactNoIncentives;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        irr_with_ca_pct: m.outrightIRR != null ? (m.outrightIRR * 100).toFixed(1) : '—',
        irr_no_ca_pct: m.outrightIRRWithoutCA != null ? (m.outrightIRRWithoutCA * 100).toFixed(1) : '—',
        lcoe: fmt3(m.lcoe || 0),
        gross_20yr_revenue: fmt(grossRevenue20yr),
        total_ca_claim: fmt(m.cumCATaxSaving || 0),
        total_gita_claim: fmt(m.itaCredit || 0),
        // Sales person
        sales_person_name: sales.name || '—',
        sales_person_title: sales.title || 'Solar Consultant',
        sales_person_email: sales.email || '',
        sales_person_whatsapp: sales.whatsapp || '',
        sales_person_photo: sales.photoUrl || '',
        sales_person_initial: (sales.name || 'A').trim().charAt(0).toUpperCase(),
        // Dates
        date_issued: dates.issued || todayDMY(),
        date_valid_until: dates.valid || plus30DMY(),
        // Financing comparison summary
        comparison_title: comparisonTitle,
        comparison_subtitle: comparisonSubtitle,
    };
}

export function cscSubstitute(html, map) {
    let out = html;
    for (const key of Object.keys(map)) {
        out = out.split(`{{${key}}}`).join(map[key] != null ? String(map[key]) : '');
    }
    return out;
}

// Drop unticked financing sections from the rendered HTML.
// Comparison summary page (#p16) adapts: columns drop one at a time as
// options are unticked; the whole page is removed only if fewer than 2
// financing options remain (no comparison is meaningful with 0 or 1).
export function postProcessCscProposal(html, { includeSARE, includeOutright, includeLoan, includePhotos }) {
    const selectedCount = (includeSARE ? 1 : 0) + (includeOutright ? 1 : 0) + (includeLoan ? 1 : 0);
    if (includeSARE && includeOutright && includeLoan && includePhotos) return html;
    let doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); }
    catch { return html; }
    if (!includeSARE) {
        doc.querySelectorAll('.sare-section').forEach((el) => el.remove());
        doc.querySelectorAll('.col-sare').forEach((el) => el.remove());
    }
    if (!includeOutright) {
        doc.querySelectorAll('.outright-section').forEach((el) => el.remove());
        doc.querySelectorAll('.col-outright').forEach((el) => el.remove());
    }
    if (!includeLoan) {
        doc.querySelectorAll('.loan-section').forEach((el) => el.remove());
        doc.querySelectorAll('.col-loan').forEach((el) => el.remove());
    }
    if (selectedCount < 2) {
        doc.querySelectorAll('.comparison-summary-section').forEach((el) => el.remove());
    }
    if (!includePhotos) {
        doc.querySelectorAll('.site-photos-section').forEach((el) => el.remove());
    }
    return '<!doctype html>\n' + doc.documentElement.outerHTML;
}

// ── Pure PDF generator. Takes a pre-built var-map + render options instead of
// reading component state, so the Combined tab and the old Proposal tab share
// ONE PDF pipeline. Mirrors the original cscProposalTab.jsx generatePdf flow:
// template fetch (with CN/MY → EN body-sniff fallback), cscSubstitute +
// postProcessCscProposal, iframe + Poppins font-load + html2canvas/jsPDF loop,
// and pdf.save(...). Returns { ok: true } or { ok: false, error }.
export async function generateCscProposalPdf(map, { lang, includeSARE, includeOutright, includeLoan, includePhotos, customerName, kwp }) {
    try {
        const cacheBust = `?v=${Date.now()}`;
        const tplUrl = (lang === 'cn' ? '/templates/astern-proposal-csc-cn.html'
                      : lang === 'my' ? '/templates/astern-proposal-csc-my.html'
                      : '/templates/astern-proposal-csc.html') + cacheBust;
        let res = await fetch(tplUrl);
        let tpl = res.ok ? await res.text() : '';
        // Fallback to EN if the localized template doesn't exist OR if the
        // server returned the SPA index.html (prod nginx falls back to it
        // for missing static paths — body parses fine but has no .page divs).
        const looksLikeTemplate = (s) => /class\s*=\s*["']page(\s|["'])/.test(s);
        if (lang !== 'en' && (!res.ok || !looksLikeTemplate(tpl))) {
            res = await fetch(`/templates/astern-proposal-csc.html?v=${Date.now()}`);
            tpl = res.ok ? await res.text() : '';
        }
        if (!res.ok || !looksLikeTemplate(tpl)) {
            throw new Error(`Failed to fetch template (HTTP ${res.status})`);
        }
        const origin = window.location.origin;
        tpl = tpl
            .split("src='./").join(`src='${origin}/templates/`)
            .split('src="./').join(`src="${origin}/templates/`)
            .split("url('./").join(`url('${origin}/templates/`)
            .split('url("./').join(`url("${origin}/templates/`)
            .split('url(./').join(`url(${origin}/templates/`);
        const populated = postProcessCscProposal(cscSubstitute(tpl, map), { includeSARE, includeOutright, includeLoan, includePhotos });
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:794px;height:1123px;border:0;';
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        doc.open(); doc.write(populated); doc.close();
        if (doc.fonts && doc.fonts.ready) { try { await doc.fonts.ready; } catch {} }
        // Poppins is loaded via <link> (Google Fonts); explicitly force-load
        // the weights we actually use in the cover/back-cover so the font
        // is GUARANTEED rasterized before html2canvas snapshots — otherwise
        // 300/400 fall back to a system font with broken glyphs in the PDF.
        if (doc.fonts && doc.fonts.load) {
            try {
                await Promise.all([
                    doc.fonts.load('300 13pt Poppins'),
                    doc.fonts.load('400 13pt Poppins'),
                    doc.fonts.load('500 8pt Poppins'),
                    doc.fonts.load('600 11pt Poppins'),
                    doc.fonts.load('700 13pt Poppins'),
                    doc.fonts.load('800 24pt Poppins'),
                ]);
            } catch {}
        }
        const imgs = Array.from(doc.images || []);
        await Promise.all(imgs.map((img) => {
            if (img.complete && img.naturalWidth > 0) return Promise.resolve();
            return new Promise((resolve) => {
                img.addEventListener('load', () => resolve(), { once: true });
                img.addEventListener('error', () => resolve(), { once: true });
                setTimeout(() => resolve(), 8000);
            });
        }));
        await new Promise((r) => setTimeout(r, 600));
        const pages = doc.querySelectorAll('.page');
        if (!pages.length) throw new Error('Template has no .page elements.');
        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: false });
        for (let i = 0; i < pages.length; i++) {
            const canvas = await html2canvas(pages[i], { scale: 2.5, useCORS: true, logging: false, backgroundColor: '#ffffff', imageTimeout: 15000 });
            const img = canvas.toDataURL('image/jpeg', 0.98);
            if (i > 0) pdf.addPage();
            pdf.addImage(img, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
        }
        // Filename: "{Customer} - {kWp}KWP Astern Commercial Proposal.pdf"
        // Strip filesystem-illegal chars (/ \ : * ? " < > |) but keep spaces.
        const safeName = (customerName || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
        const kwpLabel = `${Number(kwp) || 0}`;
        pdf.save(`${safeName} - ${kwpLabel}KWP Astern Commercial Proposal.pdf`);
        document.body.removeChild(iframe);
        return { ok: true };
    } catch (e) {
        console.error(e);
        return { ok: false, error: `Proposal failed: ${e.message || e}` };
    }
}
